// src/components/tabs/TabWorkspace.tsx
// The workspace that replaces the single page outlet: a split tree of tab
// groups. Also the bridge to the outside world — the address the browser is at,
// links opened from the sidebar, and the keyboard.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { tabsStore } from '@/stores/TabsStore';
import { entityStore } from '@/stores/EntityStore';
import { resolveTitle } from '@/features/tabs/logic';
import { useNarrowViewport } from '@/hooks/useNarrowViewport';
import { PaneView } from './SplitView';

export const TabWorkspace = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { t } = useI18n();
  const [ready, setReady] = useState(false);
  const titleRef = useRef(t);
  titleRef.current = t;
  // A phone cannot hold two panes side by side: below the threshold the split
  // tree still exists in the store — nothing is closed or lost — but only the
  // focused group is on screen, and the layout comes back intact on a wider
  // window.
  const narrow = useNarrowViewport();
  // The key handler is bound once, so it reads the current value through a ref
  // rather than closing over the one it saw on mount.
  const narrowRef = useRef(narrow);
  narrowRef.current = narrow;

  // The browser's location opens a tab: on first load it restores the deep
  // link, and afterwards it is how navigation from outside the panes — the
  // sidebar, search, a notification — arrives here.
  useEffect(() => {
    const path = location.pathname + location.search;
    const state = location.state as { cordePaneTabId?: string; cordePaneOrigin?: boolean } | null;
    if (navigationType === 'POP') {
      const target = state?.cordePaneTabId;
      if (target && tabsStore.tabById(target)) {
        tabsStore.navigateTab(target, path);
      } else if (tabsStore.activeTab?.path !== path) {
        tabsStore.openTab(path, resolveTitle(location.pathname, titleRef.current, entityStore.currentEntity));
      }
    } else if (!state?.cordePaneOrigin) {
      // Sidebar and search links address the focused pane only. A matching
      // path in another pane must never steal focus or change that pane.
      tabsStore.openTab(path, resolveTitle(location.pathname, titleRef.current, entityStore.currentEntity));
    }
    setReady(true);
  }, [location.key, location.pathname, location.search, location.state, navigationType]);

  const reportPaneLocation = useCallback((tabId: string, path: string, replace: boolean) => {
    const current = window.location.pathname + window.location.search;
    const currentState = window.history.state?.usr as { cordePaneTabId?: string } | undefined;
    if (current === path && currentState?.cordePaneTabId === tabId) return;
    navigate(path, { replace, state: { cordePaneTabId: tabId, cordePaneOrigin: true } });
  }, [navigate]);

  // Only the tab in front sets the document title, or every mounted page would
  // take turns overwriting it. Read during render so the observer re-runs the
  // effect when it changes — a store field is not a valid hook dependency.
  const activeTitle = tabsStore.activeTab?.title ?? '';
  useEffect(() => {
    if (activeTitle) document.title = activeTitle;
  }, [activeTitle]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const group = tabsStore.focusedGroup;
      if (!group) return;

      if (e.key === 'w' && !e.shiftKey) {
        const active = group.activeTabId;
        if (active) {
          e.preventDefault();
          tabsStore.close(active);
        }
        return;
      }
      // Cmd/Ctrl + \ splits, matching the IDE it is modelled on. Not offered
      // while only one pane fits: the new pane would be created off-screen.
      if (e.key === '\\') {
        e.preventDefault();
        if (!narrowRef.current) tabsStore.splitGroupById(group.id, e.shiftKey ? 'column' : 'row');
        return;
      }
      if (e.key === 'ArrowRight' && e.altKey) {
        const ids = group.tabIds;
        const at = ids.indexOf(group.activeTabId ?? '');
        if (at >= 0 && ids.length > 1) {
          e.preventDefault();
          tabsStore.activateTab(ids[(at + 1) % ids.length]);
        }
        return;
      }
      if (e.key === 'ArrowLeft' && e.altKey) {
        const ids = group.tabIds;
        const at = ids.indexOf(group.activeTabId ?? '');
        if (at >= 0 && ids.length > 1) {
          e.preventDefault();
          tabsStore.activateTab(ids[(at - 1 + ids.length) % ids.length]);
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const visible = narrow ? tabsStore.focusedGroup ?? tabsStore.root : tabsStore.root;

  return (
    <div className="flex flex-1 min-w-0 min-h-0">
      {ready && <PaneView node={visible} reportPaneLocation={reportPaneLocation} />}
    </div>
  );
});
